import cloneDeep from "lodash/cloneDeep"
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { Field, Form } from "react-final-form"

import {
  getPageContentForEvent,
  ieltsFormIds,
  LeadSources,
} from "components/Ielts/IeltsOnboardingForms/utils"
import InputField from "components/Inputs/TextField"

import DemoFormFillOffer from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/V2/DemoFormFillOffer"
import DownloadPdfWidget from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/V2/DownloadPdfWidget"
import StudentContextText from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/V2/StudentContextText"
import SubmitButton from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/V2/SubmitButton"
import CustomInputSelectV5 from "components/Inputs/CustomInputSelectV5"
import {
  autonextDecision,
  forceAutonextDecision,
  getDependentQuestionFieldName,
  geturlToRedirect,
  indexCal,
  nameExtraSpacetriming,
} from "components/TofuOnbordingForms/utils"
import LinearProgressBar from "componentsLibrary/components/CustomLinearProgress/LinearProgressBar"
import useDevice from "hooks/useDevice"
import { State } from "Interfaces/storeSchema"
import Image from "next/image"
import { useRouter } from "next/router"
import { navRoutes } from "page_routes"
import { useSelector } from "react-redux"
import {
  trackIeltsClick,
  trackIeltsFormAsync,
  trackPageV2,
} from "scripts/segment"
import {
  getQueryParamStringFromObject,
  redirectOutFromIframe,
} from "utils/common_utils"
import { handleFormSubmissionGaEventForm } from "utils/event_utils"
import { ieltsPageName } from "utils/events-utils"
import { getUtmData } from "utils/utm_utils"
import { getBaselineText } from "./ieltsEliteFormJsonV2"

type PageProps = {
  isLoggedIn: undefined | boolean
  loggedInNumber: string | number | null
  formJson: any
  prefilledData?: Record<string, any>
  isMasterclassAlias?: boolean
}

const IeltsEliteProgressiveFormV2: FunctionComponent<PageProps> = ({
  isLoggedIn,
  loggedInNumber,
  formJson,
  prefilledData,
  isMasterclassAlias,
}) => {
  const router = useRouter()
  const [currentFormStep, setCurrentFormStep] = useState(1)
  const [duplicateFormValues, setDuplicateFormValues] = useState<any>({})
  const [formValues, setFormValues] = useState<any>({})
  const [isClickingBack, setIsClickingBack] = useState<boolean>(false)
  const delayForStepChange = useRef<number>(0)
  const { isMobile } = useDevice()
  const stage = useSelector((state: State) => state.spotCounselling.stage)

  //Autonext logic
  useEffect(() => {
    if (currentFormStep < formJson?.page_distribution?.length) {
      autonextDecision(
        duplicateFormValues,
        formJson,
        currentFormStep,
        setCurrentFormStep,
        isClickingBack,
      )
    }
    return () => {
      clearTimeout(delayForStepChange.current)
    }
  }, [duplicateFormValues])

  useEffect(() => {
    if (currentFormStep < formJson?.page_distribution?.length) {
      forceAutonextDecision(
        formJson,
        currentFormStep,
        formValues,
        setCurrentFormStep,
        isClickingBack,
      )
    }
    trackPageV2(ieltsPageName.DEMO_PROG_FORM, {
      pageNumber: currentFormStep,
      pageContent: getPageContentForEvent(currentFormStep),
    })
    if (currentFormStep > 1 && !isClickingBack) {
      trackIeltsClick(ieltsPageName.DEMO_PROG_FORM, {
        contentName: "Next",
        pageNumber: currentFormStep - 1,
        pageContent: getPageContentForEvent(currentFormStep - 1),
      })
    }
  }, [currentFormStep])

  const handleSaveData = async (values: any) => {
    const domainName = geturlToRedirect()
    const utmDetails = getUtmData(null)
    const newValues = cloneDeep(values)
    newValues.utmSource = utmDetails?.utmSource || ""
    newValues.utmCampaign = utmDetails?.utmCampaign || ""
    newValues.utmMedium = utmDetails?.utmMedium || ""
    newValues.utmTerm = utmDetails?.utmTerm || ""
    newValues.campaignType = utmDetails?.campaignType || ""
    newValues.adId = utmDetails?.adId || ""
    newValues.gclid = utmDetails?.gclid || ""
    newValues.adName = utmDetails?.adName || ""
    newValues.formId = ieltsFormIds.DEMO_PROG_FORM
    newValues.leadSource = router.query.leadSource ?? LeadSources.OTHER

    if (newValues?.name) {
      newValues.name = nameExtraSpacetriming(newValues?.name)
    }
    if (newValues?.howLeapHelp) {
      newValues.howLeapHelp = newValues.howLeapHelp.join(",")
    }

    const utmObject: any = {
      utm_campaign: utmDetails?.utmCampaign,
      utm_source: utmDetails?.utmSource,
      utm_term: utmDetails?.utmTerm,
      utm_medium: utmDetails?.utmMedium,
      gclid: utmDetails?.gclid,
      adName: utmDetails?.adName,
      campaign_type: utmDetails?.campaignType,
      ad_id: utmDetails?.adId,
    }

    if (loggedInNumber) {
      newValues.phone = loggedInNumber
    }
    await trackIeltsFormAsync("Form Submitted", { ...newValues, ...utmObject })
    handleFormSubmissionGaEventForm(
      { ...newValues, stage },
      {
        formType: "redirect",
        formId:
          newValues.leadSource === LeadSources?.MARKETING_DEMO_MC
            ? "Ielts Demo MC Form"
            : "Ielts Demo Form",
      },
    )

    const formValuesString = getQueryParamStringFromObject(newValues)
    const redirectURL = `${domainName}/${navRoutes.IELTS_DEMO_QL_PAGE}?prefilled_phone=${newValues.phone}&flowType=Elite Demo`
    const utmString = getQueryParamStringFromObject(utmObject)
    const finalString = redirectURL + utmString + formValuesString
    redirectOutFromIframe(finalString)
  }

  const savePersonalInfo = async (values: Record<string, any>) => {
    if (currentFormStep == formJson?.page_distribution?.length) {
      trackIeltsClick(ieltsPageName.DEMO_PROG_FORM, {
        contentName: "Send OTP",
        pageNumber: currentFormStep,
        pageContent: getPageContentForEvent(currentFormStep),
      })
      const saveValue = { ...prefilledData, ...values }
      handleSaveData(saveValue)
    } else {
      setCurrentFormStep((step) => step + 1)
    }
  }

  const getCtaText = useCallback(() => {
    if (currentFormStep) {
      if (currentFormStep == formJson?.page_distribution?.length)
        if (isMasterclassAlias) return "Select Free Masterclass Slot"
        else return "Select a Free Slot"
      else {
        return "Next"
      }
    }
  }, [currentFormStep])

  const goBackButton = (
    <div
      className="max-w-fit text-gray-1741  items-center pt-4 md:pl-0 pl-2 cursor-pointer my-4 hidden md:flex"
      onClick={() => {
        setCurrentFormStep((step) => step - 1)
        setDuplicateFormValues({})
        setIsClickingBack(true)
      }}
    >
      <img
        src={`${process.env.CDN_URL}/assets/icons/left_triangle_arrow.svg`}
        alt=""
        width="30px"
        height="30px"
      />
      <p className="text-font16 pl-2 text-[#1B1D6A] opacity-80 hidden md:block">
        Go Back
      </p>
    </div>
  )

  const backImageMob = (
    <img
      src={`${process.env.CDN_URL}/assets/icons/left_triangle_arrow.svg`}
      alt=""
      width="25px"
      height="25px"
      onClick={() => {
        setCurrentFormStep((step) => step - 1)
        setDuplicateFormValues({})
        setIsClickingBack(true)
      }}
      className="md:hidden absolute left-0 top-0 z-20"
    />
  )

  const RenderFields = ({
    component_type,
    question_text,
    options,
    validation,
    fieldValue,
    label,
    default_visibility,
    visibility_dependency,
    values,
    numGridColumns,
    question_id,
    isMultiSelect = false,
    maxSelect = 1,
    handleSubmit,
    submitting,
  }: any) => {
    const getButton = () => {
      if ((fieldValue === "emailId" && isLoggedIn) || fieldValue === "phone") {
        return (
          <SubmitButton
            ctaText={getCtaText()}
            handleSubmit={handleSubmit}
            submitting={submitting}
          />
        )
      }
    }

    let isThisFieldVisible = true
    for (let i = 0; i < visibility_dependency?.dependencies?.length; i++) {
      if (
        visibility_dependency?.dependencies[i]?.value?.includes(
          values[
            getDependentQuestionFieldName(
              formJson,
              visibility_dependency?.dependencies[i]?.question_id,
            )
          ],
        )
      ) {
        continue
      } else {
        isThisFieldVisible = false
        break
      }
    }
    if (isThisFieldVisible || default_visibility)
      switch (component_type) {
        case "single_select_tile": {
          return (
            <>
              <p
                className={` text-black md:text-xl font-bold text-center relative ${
                  question_id === 1 ? "md:pt-[70px]" : null
                }`}
              >
                {currentFormStep > 1 && backImageMob}
                {question_text}
              </p>
              <div
                className={"md:py-2 px-1 py-2 "}
                onMouseDown={(event: any) => {
                  event.stopPropagation()
                  event.preventDefault()
                }}
              >
                <Field
                  name={fieldValue}
                  label={label}
                  component={CustomInputSelectV5}
                  options={options}
                  isMultiSelect={isMultiSelect}
                  maxSelect={maxSelect}
                  numGridColumns={numGridColumns ?? null}
                  validate={() => {}}
                  onClick={(e: any) => {
                    setIsClickingBack(false)
                    if (!isMultiSelect && formJson?.is_auto_progressive) {
                      delayForStepChange.current = window.setTimeout(() => {
                        setDuplicateFormValues({
                          ...duplicateFormValues,
                          [fieldValue]: e?.target?.value,
                        })
                      }, 200)
                    }
                  }}
                  checkBoxVersion={8}
                  ieltsQe={true}
                />
              </div>
              {values[fieldValue] && (
                <SubmitButton
                  ctaText={getCtaText()}
                  handleSubmit={handleSubmit}
                  submitting={submitting}
                />
              )}
            </>
          )
        }

        case "numberInput": {
          return (
            <>
              <p className={`text-black md:text-xl font-bold`}>
                {question_text}
              </p>
              <div className="md:px-7 md:py-2 px-4 py-2 w-full md:w-4/5 md:mx-auto shadow-4">
                <Field
                  name={fieldValue}
                  component={InputField}
                  placeholder={label}
                  label={"label"}
                  type="number"
                  validate={(v) => {
                    if (formJson?.required.includes(fieldValue) && !v)
                      return "This is a mandatory field"
                    if (v && validation.pattern) {
                      const regex = new RegExp(validation?.pattern)
                      if (!regex.test(v)) return validation.errorMessage
                    }
                  }}
                />
              </div>
            </>
          )
        }

        case "textInput": {
          if ((fieldValue === "phone" && !isLoggedIn) || fieldValue !== "phone")
            return (
              <>
                <div className="mb-4">
                  <p className={`text-black md:text-xl font-bold`}>
                    {question_text}
                  </p>
                  <div>
                    <div className=" md:py-2  py-2  w-full ">
                      <Field
                        name={fieldValue}
                        component={InputField}
                        placeholder={label}
                        label={label}
                        type="text"
                        validate={(v) => {
                          if (formJson?.required.includes(fieldValue) && !v)
                            return "This is a mandatory field"
                          if (v && !v.trim())
                            return "Please enter correct answer"

                          if (v && validation.pattern) {
                            const regex = new RegExp(validation?.pattern)
                            if (!regex.test(v)) return validation.errorMessage
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                {getButton()}
              </>
            )
        }
      }
  }

  return (
    <div className="relative flex flex-col justify-between !min-h-[100vh] h-full w-full">
      <div className="">
        <LinearProgressBar
          width={Math.round(
            (currentFormStep / formJson?.page_distribution?.length) * 100,
          )}
          color="#5CB9B6"
        />
        <div className="px-4 md:px-0 md:max-w-[900px] mx-auto rounded-10 relative z-10">
          <div className="md:px-10 mt-8 md:mt-6">
            {currentFormStep < 4 && (
              <div className="">
                <StudentContextText currentFormStep={5} />
              </div>
            )}
            {currentFormStep >= 4 && (
              <div className="relative h-24 flex justify-center items-center">
                {!isMobile && (
                  <>
                    {backImageMob}
                    <Image
                      src={`${process.env.CDN_URL}/assets/images/ielts/congrats_confetti_text.svg`}
                      layout="fill"
                    />
                  </>
                )}
                <div className="z-10 flex flex-col items-center md:gap-5 gap-1">
                  <p className="font-bold text-[#0A0926] text-[20px] md:text-[24px]">
                    Crack{" "}
                    <span className="bg-gradient-to-r from-[#635EFF] via-[#663CFF] to-[#5200FF]  text-white px-3 rounded-3 inline-block skew-x-[-10deg]">
                      <span className="inline-block skew-x-[10deg]">IELTS</span>
                    </span>{" "}
                    in 1st attempt
                  </p>
                  {isMobile ? (
                    <hr className="border border-[#E3E3FD] w-[100%] my-5"></hr>
                  ) : null}
                  <p className="text-[#0A0926] opacity-70 text-base font-semibold">
                    {isMasterclassAlias
                      ? "Enter your details to confirm your masterclass"
                      : "Enter your details to confirm your demo class"}
                  </p>
                </div>
              </div>
            )}

            {currentFormStep > 1 && goBackButton}
            <div
              className={`flex flex-col justify-between md:max-w-[600px] m-auto`}
            >
              <div>
                <Form onSubmit={savePersonalInfo} initialValues={prefilledData}>
                  {({ handleSubmit, values, submitting }) => {
                    setFormValues(values)

                    return (
                      <div>
                        <form
                          onSubmit={handleSubmit}
                          noValidate
                          className="md:min-h-[300px] mt-8"
                        >
                          {formJson?.fields
                            .slice(
                              indexCal(formJson, currentFormStep)[0],
                              indexCal(formJson, currentFormStep)[1],
                            )
                            .map((field: any) => {
                              const {
                                type,
                                options,
                                question_text,
                                fieldValue,
                                label,
                                validation,
                                default_visibility,
                                visibility_dependency,
                                numGridColumns,
                                question_id,
                                isMultiSelect,
                                maxSelect,
                              } = field
                              return (
                                <>
                                  {RenderFields({
                                    component_type: type,
                                    question_text,
                                    options,
                                    validation,
                                    fieldValue,
                                    label,
                                    default_visibility,
                                    visibility_dependency,
                                    values,
                                    numGridColumns,
                                    question_id,
                                    isMultiSelect,
                                    maxSelect,
                                    handleSubmit,
                                    submitting,
                                  })}
                                </>
                              )
                            })}
                        </form>
                      </div>
                    )
                  }}
                </Form>
              </div>
            </div>
            {currentFormStep === formJson?.page_distribution?.length && (
              <div className="px-4 md:px-0 flex justify-center md:items-center flex-col gap-4 pb-8 py-4">
                <DownloadPdfWidget />
              </div>
            )}
          </div>
        </div>
      </div>
      {currentFormStep === formJson?.page_distribution?.length ? (
        <div className="px-4 md:px-0 flex justify-center md:items-center flex-col gap-4 pb-8">
          <DemoFormFillOffer />
        </div>
      ) : (
        <p className="bg-[#F8F8FF] rounded-6 flex gap-x-2 py-3 px-4 md:px-12 items-center justify-center mt-4 w-fit mx-auto mb-16">
          <img
            src={`${process.env.CDN_URL}/assets/icons/yellow_bulb.svg`}
            alt="bulb_icon"
            width="20px"
            height="20px"
          />
          <span className="text-[12px] md:text-[15px] text-[#424242] inline-block min-w-fit">
            {getBaselineText(currentFormStep)}
          </span>
        </p>
      )}
      <Image
        src={
          isMobile
            ? `${process.env.CDN_URL}/assets/images/ielts/ielts_qe_bg_mob.svg`
            : `${process.env.CDN_URL}/assets/images/ielts/ielts_qe_bg_web.png`
        }
        layout="fill"
        objectFit="fill"
        className="z-[-2]"
      />
    </div>
  )
}

export default IeltsEliteProgressiveFormV2
